"use client";
import PropertyPageLayout from "@/components/PropertyPageLayout";
import PropertyContainer from "@/components/PropertyContainer";
import { IPropertyResult } from "@/interfaces/property";
import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import i18n, { getCurrentLanguage } from "../lib/i18n";
import { traceApiError, traceApiResponse } from "@/lib/api-tracing";
import { apiFetch } from "@/lib/api-auth";
import { API_BASE } from "@/lib/api";

const Property = () => {
  const { uid, profile } = useParams();
  const navigate = useNavigate();
  const [property, setProperty] = useState<IPropertyResult>();
  // Preview signal lives in the X-Getlost-Preview response header, NOT on
  // the IPropertyResult body shape. The backend sets it when the owner
  // or a platform admin reads an unpublished property; we read it from
  // the response object so the body stays free of control flags.
  const [isPreview, setIsPreview] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [shouldNavigateToNotFound, setShouldNavigateToNotFound] = useState(false);

  // DEV ENV ONLY!! - Fallback UID if none is provided
  const propertyUid = uid || "107d03f7-aa82-4b6e-a1c4-1586f7482404";

  const fetchProperty = async (isLanguageChange = false) => {
    if (!isLanguageChange) {
      setLoading(true);
    }
    setError(false);
    const startTime = performance.now();
    const url = `${API_BASE}/profile/${profile}/property/${propertyUid}`;
    try {
      // Protocol separation: apiFetch does NOT send the admin access
      // token in the Authorization header. Admin auth travels via the
      // accessToken cookie; AuthCookie on the backend reads it and
      // sets req.adminUser. The Authorization header is reserved for
      // Firebase ID tokens (iOS GraphQL path) — caller-supplied only.
      //
      // On 401 apiFetch retries once: POST /admin/v1/refresh/ rotates
      // the access+refresh cookies, then replays the request. The
      // replay has no Authorization header; the backend reads the
      // (now-fresh) accessToken cookie and the request succeeds for
      // the public skip-route endpoint. If refresh itself fails (no
      // refresh cookie, expired refresh cookie), the retry still has no
      // Authorization header and the backend either serves
      // anonymously (skip route) or returns 401 (protected route).
      const response = await apiFetch(url, {
        method: "GET",
          headers: {
            "Content-Type": "application/json",
            "Accept-Language": getCurrentLanguage(),
          },
        }
      );

      if (response.status === 404) {
        setError(true);
        setShouldNavigateToNotFound(true);
        return;
      }

      // If the public skip-route endpoint still returned 401 after
      // the apiFetch refresh dance, the backend is misconfigured or
      // the route was changed. Surface as a not-found rather than
      // crashing the page.
      if (response.status === 401) {
        setError(true);
        setShouldNavigateToNotFound(true);
        return;
      }

      const previewHeader = response.headers.get("X-Getlost-Preview");
      const data = await response.json();
      const duration = performance.now() - startTime;
      traceApiResponse(
        url,
        'GET',
        response,
        data,
        duration,
        { profile, propertyUid, operation: 'fetch_property' }
      );
      // Handle both nested and flat response structures
      const normalizedData = data.data ? data.data : data;
      setProperty(normalizedData);
      setIsPreview(previewHeader === "1");
    } catch (error) {
      const duration = performance.now() - startTime;
      traceApiError(
        url,
        'GET',
        error as Error,
        { profile, propertyUid, operation: 'fetch_property', duration }
      );
      console.error(error);
      setError(true);
      setShouldNavigateToNotFound(true);
    } finally {
      if (!isLanguageChange) {
        setLoading(false);
      }
    }
  };

  useEffect(() => {
    fetchProperty();

    // Listen for language change
    const onLangChange = () => fetchProperty(true);
    i18n.on('languageChanged', onLangChange);
    return () => {
      i18n.off('languageChanged', onLangChange);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [profile, propertyUid]);

  // Handle navigation to notfound
  useEffect(() => {
    if (shouldNavigateToNotFound) {
      navigate("/notfound", { replace: true });
    }
  }, [shouldNavigateToNotFound, navigate]);

  // Show loading state
  if (loading) {
    return (
      <div className="flex items-center justify-center w-full h-screen">
        <div className="flex items-center gap-2">
          <div className="w-4 h-4 border-2 border-tertiary border-t-transparent rounded-full animate-spin"></div>
          <span className="text-tertiary">Loading...</span>
        </div>
      </div>
    );
  }

  // Show error state or wait for navigation
  if (error || !property) {
    return (
      <div className="flex items-center justify-center w-full h-screen">
        <div className="flex items-center gap-2">
          <span className="text-tertiary">Redirecting...</span>
        </div>
      </div>
    );
  }

  // Meta tags for SEO and Social media
  const title = property?.name + " - " + property.profile.name + " - GetLost";
  const description = Array.isArray(property.description)
    ? property.description
        .map((item) =>
          typeof item.content === "string"
            ? item.content
            : Array.isArray(item.content)
            ? item.content.join(", ")
            : ""
        )
        .join(" ")
        .slice(0, 160) + "..."
    : "";
  const photo = property.photo ? property.photo.url : null;
  const photoWidth = property.photo ? property.photo.width.toString() : "";
  const photoHeight = property.photo ? property.photo.height.toString() : "";
  const siteName = "GetLost";

  return (
    <>
      <Helmet>
        <title>{title}</title>
        <meta name="description" content={description} />
        <meta property="og:title" content={title} />
        <meta property="og:description" content={description} />
        <meta
          property="og:url"
          content={`https://app.getlost.me/${property.profile.id}/property/${property.uid}`}
        />
        <meta property="og:type" content="website" />
        <meta property="og:site_name" content={siteName} />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={title} />
        <meta name="twitter:description" content={description} />

        {photo && <meta property="og:image" content={photo} />}
        {photo && <meta property="og:image:secure_url" content={photo} />}
        {photo && <meta property="og:image:type" content="image/jpeg" />}
        {photo && <meta property="og:image:width" content={photoWidth} />}
        {photo && <meta property="og:image:height" content={photoHeight} />}
        {photo && <meta name="twitter:image" content={photo} />}
      </Helmet>
      {isPreview && (
        <div
          role="status"
          aria-live="polite"
          data-testid="property-preview-banner"
          className="w-full bg-amber-100 border-b border-amber-300 text-amber-900 px-4 py-2 text-sm flex items-center justify-center gap-2"
        >
          <span className="font-semibold">Preview mode</span>
          <span aria-hidden="true">·</span>
          <span>You are previewing this property. Only you can see it.</span>
        </div>
      )}
      <PropertyPageLayout
        detailsContent={<PropertyContainer property={property} />}
        property={property}
        profileSlug={profile}
      />
    </>
  );
};

export default Property;
